import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
} from '@mui/material';
import { 
  Close as CloseIcon, 
  Person, 
  Work, 
  Schedule,
  TrendingUp,
  Star,
  Phone,
  Email,
  CalendarToday,
  Description,
  ExpandMore,
  ExpandLess,
  Info,
  Gavel,
  People,
  AttachMoney,
  AccountBalance,
  VerifiedUser,
  CheckCircle,
  Route,
  LocationOn,
  Delete,
  Warning,
  PersonAdd,
  FileDownload,
  ContentCopy,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import ViolationsTab from './ViolationsTab';
import CustomerManagement from './CustomerManagement';
import RouteOptimizationDashboard from './RouteOptimizationDashboard';
import ThisWeeksSchedule from './ThisWeeksSchedule';
import TodaysJobs from './TodaysJobs';
import { useScheduleData } from '../hooks/useScheduleData';
import PaymentVerificationStatus from './PaymentVerificationStatus';
import { getAccountStatus as getAccountStatusUtil, hasPaymentSetup as hasPaymentSetupUtil } from '../utils/customerStatusUtils';
import { ExistingCustomer } from '../types';

interface ProviderDetailDialogProps {
  open: boolean;
  onClose: () => void;
  providerId: string;
  providerName: string;
  onVerificationChange?: () => void; // Optional callback to refresh parent data after verification
}

interface ProviderData {
  provider: {
    id: string;
    businessName: string;
    businessLogo?: string | null;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    address?: { street: string; city: string; state: string; zipCode: string } | null;
    rating: number;
    totalJobs: number;
    completedJobs: number;
    isVerified: boolean;
    createdAt: string;
    serviceAreas: string[];
    services: string[];
    crewSize: number;
    stripeAccountStatus?: {
      hasAccount: boolean;
      accountId?: string;
      onboardingComplete: boolean;
      readyForPayouts: boolean;
      chargesEnabled?: boolean;
      payoutsEnabled?: boolean;
      error?: string;
    } | null;
    insuranceInfo?: {
      insuranceProvider?: string;
      insurancePolicyNumber?: string;
      insuranceExpiryDate?: string;
      insuranceVerified: boolean;
      insuranceProof?: string;
    };
    w9DocumentUrl?: string;
    w9OnFile?: boolean;
    pesticideLicenseDocumentUrl?: string;
    pesticideLicenseNumber?: string;
    pesticideLicenseExpiryDate?: string;
    pesticideLicenseOnFile?: boolean;
  };
  allCustomers?: any[];
  pendingBids: any[];
  todaysJobs: any[];
  weeklyJobs: any[];
  allBids: any[];
  onboardedCustomers?: any[];
  violations?: {
    total: number;
    pendingFines: number;
    totalPendingFines: number;
    disputedViolations: number;
    hasOverdueFines: boolean;
    list: any[];
  };
  analytics?: {
    paymentStats: {
      totalRevenue: number;
      totalPayouts: number;
      totalEarnings: number;
      averageTransaction: number;
      transactionCount: number;
    };
    recentPayments: any[];
  };
}

const ProviderDetailDialog: React.FC<ProviderDetailDialogProps> = ({
  open,
  onClose,
  providerId,
  providerName,
  onVerificationChange
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [data, setData] = useState<ProviderData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedBidId, setExpandedBidId] = useState<string | null>(null);
  const [bidHistoryData, setBidHistoryData] = useState<{[key: string]: any}>({});
  const [onboardedCustomersList, setOnboardedCustomersList] = useState<ExistingCustomer[]>([]);
  const [loadingOnboardedCustomers, setLoadingOnboardedCustomers] = useState(false);
  const [onboardedCustomersServiceRequests, setOnboardedCustomersServiceRequests] = useState<Record<string, any[]>>({});
  const [documentDialogOpen, setDocumentDialogOpen] = useState(false);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [documentTitle, setDocumentTitle] = useState<string>('');
  const [documentLoading, setDocumentLoading] = useState(false);
  const [documentError, setDocumentError] = useState<string | null>(null);
  const [documentBlobUrl, setDocumentBlobUrl] = useState<string | null>(null); // For W-9: revoke on close
  
  // Use schedule data hook with providerId (for Admin viewing provider's schedule)
  const {
    scheduledJobs,
    todaysJobs,
    loading: scheduleDataLoading,
    error: scheduleDataError,
    refreshSchedules,
  } = useScheduleData(providerId);
  
  // Listen for schedule update events to auto-refresh (same as Provider Dashboard)
  useEffect(() => {
    const handleScheduleUpdate = async () => {
      console.log('[ADMIN-SCHEDULE-UPDATE] Event received, refreshing schedules...');
      await refreshSchedules();
      console.log('[ADMIN-SCHEDULE-UPDATE] Schedules refreshed');
    };
    
    window.addEventListener('customer-schedule-updated', handleScheduleUpdate);
    window.addEventListener('refresh-todays-schedule', handleScheduleUpdate);
    
    return () => {
      window.removeEventListener('customer-schedule-updated', handleScheduleUpdate);
      window.removeEventListener('refresh-todays-schedule', handleScheduleUpdate);
    };
  }, [refreshSchedules]);

  // Helper functions for schedule components (mirror Provider Dashboard)
  const formatServiceTypeName = (serviceTypeName: string | undefined): string => {
    if (!serviceTypeName) return 'Service';
    return serviceTypeName
      .split(', ')
      .map((s: string) => {
        const normalized = s.trim().toLowerCase();
        if (normalized === 'mowing' || normalized === 'edging' || 
            normalized.includes('mowing') || normalized.includes('trim') || normalized.includes('edge')) {
          return 'Lawn Mowing/Trimming';
        }
        if (s === 'fertilizingWeedControl' || normalized.includes('fertilizing') || normalized.includes('weed')) {
          return 'Weed Control/Fertilizing';
        }
        return s.charAt(0).toUpperCase() + s.slice(1);
      })
      .filter((name: string, index: number, arr: string[]) => arr.indexOf(name) === index)
      .join(', ');
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'pending': return 'warning';
      case 'bidding': return 'info';
      case 'accepted': return 'success';
      case 'scheduled': return 'info';
      case 'in-progress': return 'primary';
      case 'completed': return 'success';
      case 'rejected': return 'error';
      case 'cancelled': return 'error';
      case 'pending_cancellation': return 'warning'; // Warning color for pending cancellation
      default: return 'default';
    }
  };

  // Handlers for schedule components (Admin can perform same actions as Provider)
  const handleEditSchedule = (job: any) => {
    // For Admin, we can show a message or implement edit functionality
    // For now, just log - can be enhanced later
    console.log('[ADMIN] Edit schedule for job:', job);
    alert('Schedule editing from Admin view - functionality can be added if needed');
  };

  const handleCompleteJob = (job: any) => {
    // For Admin, we can show a message or implement completion functionality
    console.log('[ADMIN] Complete job:', job);
    alert('Job completion from Admin view - functionality can be added if needed');
  };

  const handleRetryPayment = async (jobId: string) => {
    if (!window.confirm('Retry payment processing for this job?')) {
      return;
    }
    try {
      const response = await apiService.post(`/payments/retry-payment/${jobId}`);
      if (response.success) {
        alert('Payment retry initiated');
        await refreshSchedules();
        await fetchProviderDetails();
      } else {
        alert('Failed to retry payment: ' + (response.error || 'Unknown error'));
      }
    } catch (err: any) {
      console.error('Failed to retry payment:', err);
      alert('Failed to retry payment: ' + (err.message || 'Unknown error'));
    }
  };

  const [paymentStatuses] = useState<Map<string, any>>(new Map());
  const [retryingPayment, setRetryingPayment] = useState<string | null>(null);

  const fetchProviderDetails = useCallback(async () => {
    const id = providerId && (typeof providerId === 'string' ? providerId : (providerId as any)?._id || String(providerId));
    if (!id) {
      setError('Provider ID is missing');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.get(`/admin/provider/${id}`);
      // apiService.get returns response.data directly from axios
      // Backend sends: { success: true, data: { provider: {...}, ... } }
      // axios returns: { data: { success: true, data: { provider: {...}, ... } } }
      // apiService.get returns: { success: true, data: { provider: {...}, ... } }
      const responseData = response as any;
      console.log('[ProviderDetailDialog] Full apiService response:', JSON.stringify(responseData, null, 2));
      console.log('[ProviderDetailDialog] response.data:', responseData.data);
      console.log('[ProviderDetailDialog] response.success:', responseData.success);
      
      // Handle both wrapped ({success: true, data: {...}}) and unwrapped ({provider: {...}}) responses
      const actualData = responseData.data || responseData;
      console.log('[ProviderDetailDialog] actualData:', actualData);
      console.log('[ProviderDetailDialog] actualData.provider:', actualData?.provider);
      console.log('[ProviderDetailDialog] Insurance info:', actualData?.provider?.insuranceInfo);
      console.log('[ProviderDetailDialog] Provider keys:', actualData?.provider ? Object.keys(actualData.provider) : 'no provider');
      setData(actualData);
      
      // Debug: Log all bids to see what we're receiving - EXPANDED VERSION

    } catch (err: any) {
      const backendError = err.response?.data?.error || err.response?.data?.message;
      const message = backendError || err.message || 'Failed to fetch provider details';
      console.error('[ProviderDetailDialog] Failed to fetch provider details:', { providerId, error: message, response: err.response?.data });
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [providerId]);

  useEffect(() => {
    if (open && providerId) {
      fetchProviderDetails();
      fetchOnboardedCustomers();
      // Refresh schedules when dialog opens (useScheduleData will handle the fetch)
      refreshSchedules().catch(err => {
        console.error('[ADMIN] Error refreshing schedules on dialog open:', err);
      });
    }
  }, [open, providerId, fetchProviderDetails, refreshSchedules]);

  // Fetch onboarded customers (mirror CustomerManagement component)
  const fetchOnboardedCustomers = useCallback(async () => {
    setLoadingOnboardedCustomers(true);
    try {
      // Pass providerId as query parameter for Admin requests
      const params = providerId ? { providerId } : {};
      const response = await apiService.get('/route-optimization/existing-customers', params);
      if (response.success && Array.isArray(response.data)) {
        const customers = response.data as ExistingCustomer[];
        // Filter onboarded customers: same logic as CustomerManagement
        const isMarketplaceCustomer = (customer: ExistingCustomer): boolean =>
          (customer.customerSource || '').toLowerCase() === 'bid';
        
        const onboarded = customers.filter(customer => 
          !isMarketplaceCustomer(customer) &&
          customer.linkedCustomerId &&
          hasPaymentSetupUtil(customer, customers)
        );
        setOnboardedCustomersList(onboarded);
        
        // Also fetch service requests for onboarded customers (like CustomerManagement does)
        // This ensures services added via accepted bids show up even if servicePricing hasn't been updated yet
        try {
          const serviceRequestsResponse = await apiService.get('/service-requests', {
            userType: 'provider',
            status: 'accepted,scheduled,completed',
            limit: 1000, // Get all ServiceRequests for provider detail dialog (backend skips pagination when limit >= 1000)
            page: 1,
            providerId: providerId // Filter by providerId to only get service requests for this provider
          });
          
          if (serviceRequestsResponse.success && serviceRequestsResponse.data) {
            const allRequests = (serviceRequestsResponse.data as any).serviceRequests || [];
            
            // Group service requests by customer linkedCustomerId
            const requestsByCustomer: Record<string, any[]> = {};
            
            onboarded.forEach((customer) => {
              const customerId = customer.id || customer._id || '';
              if (customer.linkedCustomerId && customerId) {
                const customerRequests = allRequests.filter((sr: any) => {
                  const srCustomerId = sr.customerId?._id || sr.customerId?.id || sr.customerId;
                  return srCustomerId?.toString() === customer.linkedCustomerId?.toString();
                });
                requestsByCustomer[customerId] = customerRequests;
              }
            });
            
            setOnboardedCustomersServiceRequests(requestsByCustomer);
          }
        } catch (err: any) {
          console.error('[ADMIN] Error fetching service requests for onboarded customers:', err);
          setOnboardedCustomersServiceRequests({});
        }
      } else {
        setOnboardedCustomersList([]);
        setOnboardedCustomersServiceRequests({});
      }
    } catch (err: any) {
      console.error('Error fetching onboarded customers:', err);
      setOnboardedCustomersList([]);
      setOnboardedCustomersServiceRequests({});
    } finally {
      setLoadingOnboardedCustomers(false);
    }
  }, [providerId]);

  // Helper functions (mirror CustomerManagement component)
  const isMarketplaceCustomer = (customer: ExistingCustomer): boolean =>
    (customer.customerSource || '').toLowerCase() === 'bid';

  const generateServiceBreakdown = (customer: ExistingCustomer) => {
    if (customer.servicePricing) {
      return customer.servicePricing;
    }
    return null;
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleBidRowClick = async (bidId: string, request: any) => {
    if (expandedBidId === bidId) {
      setExpandedBidId(null);
      return;
    }

    setExpandedBidId(bidId);

    // Extract request ID - handle both object and string, check both requestId and serviceRequestId
    const requestId = typeof request === 'object' 
      ? (request?._id || request?.id)
      : request;

    if (!requestId || requestId === 'undefined') {
      console.error('Invalid request ID:', request);
      return;
    }

    // Fetch bid history if not already loaded
    if (!bidHistoryData[requestId]) {
      try {
        const response = await apiService.get(`/service-requests/${requestId}`);
        if (response.success && response.data) {
          setBidHistoryData(prev => ({
            ...prev,
            [requestId]: response.data as any
          }));
        }
      } catch (err) {
        console.error('Failed to fetch bid history:', err);
      }
    }
  };

  const handleVerifyProvider = async (isVerified: boolean) => {
    if (!data) return;
    try {
      const response = await apiService.put(`/admin/provider/${providerId}/verify`, {
        isVerified,
      });
      if (response.success) {
        // Update local data
        const responseData = response.data as any;
        setData({
          ...data,
          provider: {
            ...data.provider,
            isVerified: responseData?.isVerified ?? isVerified,
          },
        });
        // Trigger parent refresh callback if provided
        if (onVerificationChange) {
          onVerificationChange();
        }
      }
    } catch (err: any) {
      const isTimeout = err.code === 'ECONNABORTED' || err.message?.includes('timeout') || err.message?.includes('Network error');
      const msg = isTimeout
        ? 'The request timed out. If the provider status has updated, no action is needed. Try refreshing.'
        : (err.response?.data?.error || err.message || 'Failed to update verification status');
      setError(msg);
      if (isTimeout && onVerificationChange) {
        onVerificationChange();
      }
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const renderStars = (rating: number) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Star
          key={i}
          sx={{
            fontSize: 20,
            color: i <= Math.round(rating) ? '#ffc107' : '#e0e0e0'
          }}
        />
      );
    }
    return stars;
  };


  const formatDateTime = (dateString: string | Date | null | undefined) => {
    if (!dateString) return 'Not scheduled';
    
    try {
      const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
      // Format in Central Time (America/Chicago)
      return date.toLocaleString('en-US', {
        timeZone: 'America/Chicago',
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      console.error('[ProviderDetailDialog] Error formatting date:', dateString, e);
      return 'Invalid date';
    }
  };

  const formatDateOnly = (dateString: string | Date | null | undefined) => {
    if (!dateString) return 'Not scheduled';
    
    try {
      const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
      // Format date only in Central Time (America/Chicago)
      return date.toLocaleDateString('en-US', {
        timeZone: 'America/Chicago',
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      console.error('[ProviderDetailDialog] Error formatting date:', dateString, e);
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent>
          <Alert severity="error">{error}</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  }

  if (!data) return null;

  const { provider, allCustomers = [], pendingBids, weeklyJobs, allBids, onboardedCustomers = [], violations, analytics } = data;

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth sx={{ '& .MuiDialog-paper': { height: '90vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {provider.businessLogo ? (
            <Avatar
              src={provider.businessLogo}
              alt={provider.businessName || 'Provider logo'}
              sx={{ width: 56, height: 56 }}
              variant="rounded"
            />
          ) : (
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              <Person />
            </Avatar>
          )}
          <Box>
            <Typography variant="h6">{provider.businessName || `${provider.firstName} ${provider.lastName}`}</Typography>
            <Typography variant="body2" color="text.secondary">
              Provider Details & Activity
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Provider Info Header */}
        <Box sx={{ p: 3, backgroundColor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {renderStars(provider.rating)}
                  <Typography variant="body2" color="text.secondary">
                    ({provider.rating.toFixed(1)})
                  </Typography>
                </Box>
                <Chip 
                  label={provider.isVerified ? 'Verified' : 'Unverified'} 
                  color={provider.isVerified ? 'success' : 'warning'} 
                  size="small" 
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Email sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2">{provider.email}</Typography>
              </Box>
              {provider.phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2">{provider.phone}</Typography>
                </Box>
              )}
              {provider.address && (provider.address.street || provider.address.city || provider.address.zipCode) && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                  <LocationOn sx={{ fontSize: 16, color: 'text.secondary', mt: 0.25 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Business address</Typography>
                    <Typography variant="body2">
                      {[provider.address.street, provider.address.city, provider.address.state, provider.address.zipCode].filter(Boolean).join(', ')}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Business Logo - download / copy */}
              {provider.businessLogo && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Business Logo
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Avatar
                      src={provider.businessLogo}
                      alt="Business logo"
                      variant="rounded"
                      sx={{ width: 80, height: 80, border: 1, borderColor: 'divider' }}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FileDownload />}
                        onClick={() => {
                          try {
                            const url = provider.businessLogo;
                            if (!url) return;
                            if (url.startsWith('data:')) {
                              const a = document.createElement('a');
                              a.href = url;
                              a.download = `provider-${provider.businessName || provider.id}-logo.png`;
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                            } else {
                              window.open(url, '_blank');
                            }
                          } catch (e) {
                            console.error('Download logo failed', e);
                          }
                        }}
                      >
                        Download
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ContentCopy />}
                        onClick={async () => {
                          try {
                            const url = provider.businessLogo;
                            if (url) await navigator.clipboard.writeText(url);
                          } catch (e) {
                            console.error('Copy logo failed', e);
                          }
                        }}
                      >
                        Copy link
                      </Button>
                    </Box>
                  </Box>
                </Box>
              )}
              
              {/* Insurance & Stripe Account Info */}
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                Insurance Information
              </Typography>
              
              {provider.insuranceInfo ? (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <VerifiedUser sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" fontWeight="medium">Insurance Details</Typography>
                    {provider.insuranceInfo.insuranceVerified && (
                      <Chip icon={<CheckCircle />} label="Verified" color="success" size="small" />
                    )}
                  </Box>
                  {provider.insuranceInfo.insuranceProvider ? (
                    <Typography variant="body2" color="text.primary" sx={{ ml: 4, mb: 0.5, display: 'block' }}>
                      Company: {provider.insuranceInfo.insuranceProvider}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 0.5, display: 'block' }}>
                      Company: Not provided
                    </Typography>
                  )}
                  {provider.phone ? (
                    <Typography variant="body2" color="text.primary" sx={{ ml: 4, mb: 0.5, display: 'block' }}>
                      Phone: {provider.phone}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 0.5, display: 'block' }}>
                      Phone: Not provided
                    </Typography>
                  )}
                  {provider.insuranceInfo.insurancePolicyNumber ? (
                    <Typography variant="body2" color="text.primary" sx={{ ml: 4, mb: 0.5, display: 'block' }}>
                      Policy Number: {provider.insuranceInfo.insurancePolicyNumber}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 0.5, display: 'block' }}>
                      Policy Number: Not provided
                    </Typography>
                  )}
                  {provider.insuranceInfo.insuranceExpiryDate ? (
                    <Typography variant="body2" color="text.primary" sx={{ ml: 4, mb: 0.5, display: 'block' }}>
                      Expiration Date: {new Date(provider.insuranceInfo.insuranceExpiryDate).toLocaleDateString()}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 0.5, display: 'block' }}>
                      Expiration Date: Not provided
                    </Typography>
                  )}
                  {provider.insuranceInfo.insuranceProof && (
                    <Box sx={{ ml: 4, mt: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Description />}
                        onClick={() => {
                          setDocumentUrl(provider.insuranceInfo!.insuranceProof!);
                          setDocumentTitle('Insurance Document');
                          setDocumentDialogOpen(true);
                        }}
                      >
                        View Insurance Document
                      </Button>
                    </Box>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2, display: 'block' }}>
                  No insurance information available
                </Typography>
              )}
              
              {/* W-9 Document */}
              {(provider as any).w9OnFile && (
                <>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                    W-9 Tax Form
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Description sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" fontWeight="medium">W-9 Document</Typography>
                      <Chip icon={<CheckCircle />} label="On File" color="success" size="small" />
                    </Box>
                    <Box sx={{ ml: 4, mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={documentLoading ? <CircularProgress size={16} /> : <Description />}
                        disabled={documentLoading}
                        onClick={async () => {
                          setDocumentError(null);
                          setDocumentLoading(true);
                          try {
                            const token = localStorage.getItem('token');
                            const base = process.env.REACT_APP_API_URL
                              ? (process.env.REACT_APP_API_URL.endsWith('/api') ? process.env.REACT_APP_API_URL : process.env.REACT_APP_API_URL + '/api')
                              : window.location.origin + '/api';
                            const r = await fetch(`${base}/compliance/provider/${providerId}/w9-document`, {
                              headers: { Authorization: `Bearer ${token}` },
                            });
                            if (!r.ok) {
                              const errBody = await r.json().catch(() => ({}));
                              throw new Error(errBody.error || `HTTP ${r.status}`);
                            }
                            const blob = await r.blob();
                            const url = window.URL.createObjectURL(blob);
                            setDocumentBlobUrl(url);
                            setDocumentUrl(url);
                            setDocumentTitle('W-9 Tax Form Document');
                            setDocumentDialogOpen(true);
                          } catch (err: any) {
                            setDocumentError(err.message || 'Failed to load W-9 document');
                          } finally {
                            setDocumentLoading(false);
                          }
                        }}
                      >
                        View W-9 Document
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FileDownload />}
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem('token');
                            const base = process.env.REACT_APP_API_URL
                              ? (process.env.REACT_APP_API_URL.endsWith('/api') ? process.env.REACT_APP_API_URL : process.env.REACT_APP_API_URL + '/api')
                              : window.location.origin + '/api';
                            const r = await fetch(`${base}/compliance/provider/${providerId}/w9-document`, {
                              headers: { Authorization: `Bearer ${token}` },
                            });
                            if (!r.ok) throw new Error('Download failed');
                            const blob = await r.blob();
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `W-9-${provider.businessName || provider.firstName || 'provider'}.pdf`;
                            a.click();
                            window.URL.revokeObjectURL(url);
                          } catch (err: any) {
                            setDocumentError(err.message || 'Failed to download W-9 document');
                          }
                        }}
                      >
                        Download W-9
                      </Button>
                    </Box>
                    {documentError && (
                      <Alert severity="error" sx={{ mt: 1 }} onClose={() => setDocumentError(null)}>
                        {documentError}
                      </Alert>
                    )}
                  </Box>
                </>
              )}
              
              {/* Pesticide License Document */}
              {(provider as any).pesticideLicenseDocumentUrl && (
                <>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                    Pesticide License
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Description sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2" fontWeight="medium">Pesticide License Document</Typography>
                      {(provider as any).pesticideLicenseOnFile && (
                        <Chip icon={<CheckCircle />} label="On File" color="success" size="small" />
                      )}
                    </Box>
                    {(provider as any).pesticideLicenseNumber && (
                      <Typography variant="body2" color="text.primary" sx={{ ml: 4, mb: 0.5, display: 'block' }}>
                        License Number: {(provider as any).pesticideLicenseNumber}
                      </Typography>
                    )}
                    {(provider as any).pesticideLicenseExpiryDate && (
                      <Typography variant="body2" color="text.primary" sx={{ ml: 4, mb: 0.5, display: 'block' }}>
                        Expiration Date: {new Date((provider as any).pesticideLicenseExpiryDate).toLocaleDateString()}
                      </Typography>
                    )}
                    <Box sx={{ ml: 4, mt: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Description />}
                        onClick={() => {
                          setDocumentUrl((provider as any).pesticideLicenseDocumentUrl);
                          setDocumentTitle('Pesticide License Document');
                          setDocumentDialogOpen(true);
                        }}
                      >
                        View Pesticide License Document
                      </Button>
                    </Box>
                  </Box>
                </>
              )}
              
              {/* Stripe Account Status */}
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', mt: 2 }}>
                Stripe Account Setup Status
              </Typography>
              
              {provider.stripeAccountStatus ? (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <AccountBalance sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" fontWeight="medium">Stripe Connect Account</Typography>
                    {provider.stripeAccountStatus.readyForPayouts ? (
                      <Chip icon={<CheckCircle />} label="Ready for Payouts" color="success" size="small" />
                    ) : provider.stripeAccountStatus.hasAccount ? (
                      <Chip label="Setup Incomplete" color="warning" size="small" />
                    ) : (
                      <Chip label="Not Set Up" color="error" size="small" />
                    )}
                  </Box>
                  {provider.stripeAccountStatus.hasAccount ? (
                    <>
                      {provider.stripeAccountStatus.onboardingComplete ? (
                        <Typography variant="body2" color="text.primary" sx={{ ml: 4, mb: 0.5, display: 'block' }}>
                          Onboarding: Complete
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mb: 0.5, display: 'block' }}>
                          Onboarding: Incomplete
                        </Typography>
                      )}
                      {provider.stripeAccountStatus.readyForPayouts ? (
                        <Typography variant="body2" color="success.main" sx={{ ml: 4, mb: 0.5, display: 'block' }}>
                          Status: Ready to receive payouts
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="warning.main" sx={{ ml: 4, mb: 0.5, display: 'block' }}>
                          Status: Not ready for payouts
                        </Typography>
                      )}
                      {provider.stripeAccountStatus.error && (
                        <Typography variant="caption" color="error.main" sx={{ ml: 4, display: 'block' }}>
                          Error: {provider.stripeAccountStatus.error}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 4, display: 'block' }}>
                      Provider has not set up Stripe Connect account
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2, display: 'block' }}>
                  Stripe account status unavailable
                </Typography>
              )}
              
              {/* Verification Button */}
              <Box sx={{ mt: 2 }}>
                <Button
                  variant={provider.isVerified ? "outlined" : "contained"}
                  color={provider.isVerified ? "warning" : "success"}
                  startIcon={provider.isVerified ? <CloseIcon /> : <CheckCircle />}
                  onClick={() => handleVerifyProvider(!provider.isVerified)}
                  size="small"
                >
                  {provider.isVerified ? 'Mark as Unverified' : 'Mark as Verified'}
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="primary">{provider.totalJobs}</Typography>
                      <Typography variant="caption" color="text.secondary">Total Jobs</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="success.main">{provider.completedJobs}</Typography>
                      <Typography variant="caption" color="text.secondary">Completed</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="warning.main">{pendingBids.length}</Typography>
                      <Typography variant="caption" color="text.secondary">Pending Bids</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 2, textAlign: 'center' }}>
                      <Typography variant="h4" color="info.main">{allCustomers.length}</Typography>
                      <Typography variant="caption" color="text.secondary">All Customers</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="provider details tabs" variant="scrollable" scrollButtons="auto">
            <Tab icon={<People />} label="All Customers" iconPosition="start" />
            <Tab icon={<TrendingUp />} label="Pending Bids" iconPosition="start" />
            <Tab icon={<Schedule />} label="Today's Schedule" iconPosition="start" />
            <Tab icon={<CalendarToday />} label="Weekly Schedule" iconPosition="start" />
            <Tab icon={<Description />} label="All Bids" iconPosition="start" />
            <Tab icon={<People />} label="Onboarded Customers" iconPosition="start" />
            <Tab icon={<Route />} label="Route Optimization" iconPosition="start" />
            <Tab icon={<Gavel />} label="Violations" iconPosition="start" />
            <Tab icon={<AttachMoney />} label="Analytics" iconPosition="start" />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box sx={{ height: 'calc(100% - 200px)', overflow: 'auto', p: 2 }}>
          {activeTab === 0 && (
            <Box>
              <CustomerManagement providerId={providerId} />
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  My Bids
                </Typography>
                <List>
                  {pendingBids.filter((bid: any) => bid.status !== 'accepted' && bid.status !== 'paid').length === 0 ? (
                    <ListItem>
                      <ListItemText
                        primary="No pending bids"
                        secondary="Accepted bids are managed in your customer list and schedule"
                      />
                    </ListItem>
                  ) : (
                    [...pendingBids]
                      .filter((bid: any) => bid.status !== 'accepted' && bid.status !== 'paid')
                      .sort((a: any, b: any) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()).map((bid: any) => {
                        const request = bid.requestId || bid.serviceRequestId;
                        return (
                          <ListItem key={bid._id} divider>
                            <ListItemText
                              primary={
                                <Box>
                                  {/* Customer Name - Most Prominent */}
                                  <Typography variant="h6" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                                    {typeof request !== 'string' && (request as any)?.customerId ? 
                                      `${(request as any).customerId.firstName} ${(request as any).customerId.lastName}` : 
                                      'Customer Name Unavailable'
                                    }
                                  </Typography>
                                  
                                  {/* Address */}
                                  <Typography 
                                    variant="body1" 
                                    color="text.primary" 
                                    sx={{ mb: 1 }}
                                  >
                                    <LocationOn sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                                    {typeof request !== 'string' && (request as any)?.address ? 
                                      `${(request as any).address.street}, ${(request as any).address.city}, ${(request as any).address.state} ${(request as any).address.zipCode}` : 
                                      'Address unavailable'
                                    }
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Box>
                                  {/* Property Type */}
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    <strong>Property Type:</strong> {typeof request !== 'string' && (request as any)?.propertyType ? 
                                      (request as any).propertyType : 'Not specified'}
                                  </Typography>
                                  
                                  {/* Services Requested */}
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    <strong>Services:</strong> {typeof request !== 'string' && (request as any)?.serviceType?.name ? 
                                      formatServiceTypeName((request as any).serviceType.name) : 'Services not specified'}
                                  </Typography>
                                  
                                  {/* Bid Submission Date */}
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    <strong>Bid Submitted:</strong> {formatDateOnly(bid.createdAt)}
                                  </Typography>
                                  
                                  {/* Status Chip */}
                                  <Chip
                                    label={bid.status?.charAt(0).toUpperCase() + bid.status?.slice(1)}
                                    color={getStatusColor(bid.status) as any}
                                    size="small"
                                  />
                                </Box>
                              }
                            />
                            <ListItemSecondaryAction>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {(bid.status === 'pending' || bid.status === 'rejected') && bid._id && (
                                  <IconButton
                                    edge="end"
                                    onClick={async () => {
                                      if (!window.confirm('Are you sure you want to remove this bid? This action cannot be undone.')) {
                                        return;
                                      }
                                      try {
                                        const response = await apiService.delete(`/bids/${bid._id}`);
                                        if (response.success) {
                                          await fetchProviderDetails();
                                        } else {
                                          alert('Failed to remove bid. Please try again.');
                                        }
                                      } catch (err: any) {
                                        console.error('Failed to remove bid:', err);
                                        alert('Failed to remove bid. Please try again.');
                                      }
                                    }}
                                    title="Delete Bid"
                                    color="error"
                                  >
                                    <Delete />
                                  </IconButton>
                                )}
                              </Box>
                            </ListItemSecondaryAction>
                          </ListItem>
                        );
                      })
                  )}
                </List>
              </Paper>
            </Box>
          )}

          {activeTab === 2 && (
            <Box>
              {scheduleDataLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : scheduleDataError ? (
                <Alert severity="error">{scheduleDataError}</Alert>
              ) : (
                <TodaysJobs
                  todaysJobs={todaysJobs}
                  onCompleteJob={handleCompleteJob}
                  onRetryPayment={handleRetryPayment}
                  paymentStatuses={paymentStatuses}
                  retryingPayment={retryingPayment}
                  formatServiceTypeName={formatServiceTypeName}
                  getStatusColor={getStatusColor}
                />
              )}
            </Box>
          )}

          {activeTab === 3 && (
            <Box>
              {scheduleDataLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : scheduleDataError ? (
                <Alert severity="error">{scheduleDataError}</Alert>
              ) : (
                <ThisWeeksSchedule
                  scheduledJobs={scheduledJobs}
                  onEditSchedule={handleEditSchedule}
                  formatServiceTypeName={formatServiceTypeName}
                />
              )}
            </Box>
          )}

          {activeTab === 4 && (
            <Box>
              <Typography variant="h6" gutterBottom>All Bids ({allBids.length})</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Complete record of all bids (accepted, rejected, pending, deleted, etc.)
              </Typography>
              {allBids.length === 0 ? (
                <Alert severity="info">No bids found.</Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Customer</TableCell>
                        <TableCell>Address</TableCell>
                        <TableCell>Service</TableCell>
                        <TableCell>Bid Price</TableCell>
                        <TableCell>Bid Status</TableCell>
                        <TableCell>Bid Date</TableCell>
                        <TableCell>Request Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {allBids
                        .sort((a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                        .map((bid: any) => {
                          const request = bid.requestId || bid.serviceRequestId;
                          const customerName = typeof request !== 'string' && (request as any)?.customerId
                            ? `${(request as any).customerId.firstName || ''} ${(request as any).customerId.lastName || ''}`.trim() || 'Unknown'
                            : 'Unknown';
                          const address = typeof request !== 'string' && (request as any)?.address
                            ? `${(request as any).address.street}, ${(request as any).address.city}, ${(request as any).address.state} ${(request as any).address.zipCode}`
                            : 'N/A';
                          const serviceType = typeof request !== 'string' && (request as any)?.serviceType
                            ? formatServiceTypeName((request as any).serviceType.name || (request as any).serviceType)
                            : 'N/A';
                          const bidPrice = bid.price || (bid.servicePricing && Array.isArray(bid.servicePricing) 
                            ? bid.servicePricing.reduce((sum: number, sp: any) => sum + (parseFloat(sp.price) || 0), 0)
                            : 0);
                          
                          return (
                            <TableRow key={bid._id}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {customerName}
                                </Typography>
                                {typeof request !== 'string' && (request as any)?.customerId?.email && (
                                  <Typography variant="caption" color="text.secondary">
                                    {(request as any).customerId.email}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {address}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {serviceType}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  ${bidPrice.toFixed(2)}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={bid.status?.charAt(0).toUpperCase() + bid.status?.slice(1) || 'Unknown'} 
                                  color={getStatusColor(bid.status || 'unknown') as any} 
                                  size="small" 
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {formatDateOnly(bid.createdAt)}
                                </Typography>
                                {bid.acceptedAt && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Accepted: {formatDateOnly(bid.acceptedAt)}
                                  </Typography>
                                )}
                                {bid.rejectedAt && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Rejected: {formatDateOnly(bid.rejectedAt)}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={typeof request !== 'string' ? ((request as any)?.status || 'Unknown') : 'Unknown'}
                                  color={getStatusColor(typeof request !== 'string' ? ((request as any)?.status || 'unknown') : 'unknown') as any}
                                  size="small" 
                                  variant="outlined"
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}

          {activeTab === 5 && (
            <Box>
              {loadingOnboardedCustomers ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Onboarded Customers
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Your existing customers who have created platform accounts and completed payment setup. You receive 99% of the service price (1% platform fee).
                    </Typography>
                    
                    {onboardedCustomersList.length === 0 ? (
                      <Box textAlign="center" py={4}>
                        <PersonAdd sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                          No Onboarded Customers Yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Customers will appear here once they create accounts and link to your services.
                        </Typography>
                      </Box>
                    ) : (
                      <TableContainer component={Paper}>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Customer Name</TableCell>
                              <TableCell>Property Address</TableCell>
                              <TableCell>Services & Pricing</TableCell>
                              <TableCell>Account Status</TableCell>
                              <TableCell>Payment Verification</TableCell>
                              <TableCell>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {onboardedCustomersList.map((customer) => (
                              <TableRow key={customer.id || customer._id}>
                                <TableCell>{customer.firstName} {customer.lastName}</TableCell>
                                <TableCell>
                                  {customer.address.street}, {customer.address.city}, {customer.address.state} {customer.address.zipCode}
                                </TableCell>
                                <TableCell>
                                  {(() => {
                                    const customerId = customer.id || customer._id || '';
                                    const customerRequests = onboardedCustomersServiceRequests[customerId] || [];
                                    
                                    // Check servicePricing first (like CustomerManagement does)
                                    const servicePricing = customer.servicePricing || generateServiceBreakdown(customer);
                                    
                                    // Helper to check if customer has a service (via servicePricing OR service request)
                                    const customerHasService = (serviceType: 'mowing' | 'fertilizing' | 'snow_removal'): boolean => {
                                      // Check servicePricing first
                                      const hasServicePricing = 
                                        (serviceType === 'mowing' && servicePricing?.lawnMowingTrimming?.price && 
                                         !isNaN(Number(servicePricing.lawnMowingTrimming.price)) &&
                                         Number(servicePricing.lawnMowingTrimming.price) > 0) ||
                                        (serviceType === 'fertilizing' && servicePricing?.weedControlFertilizing?.price && 
                                         !isNaN(Number(servicePricing.weedControlFertilizing.price)) &&
                                         Number(servicePricing.weedControlFertilizing.price) > 0) ||
                                        (serviceType === 'snow_removal' && servicePricing?.snowRemoval?.price && 
                                         !isNaN(Number(servicePricing.snowRemoval.price)) &&
                                         Number(servicePricing.snowRemoval.price) > 0);
                                      
                                      if (hasServicePricing) {
                                        return true;
                                      }
                                      
                                      // Also check if there's a service request for this service type
                                      // This handles onboarded customers who may have accepted a bid but servicePricing hasn't been updated yet
                                      const hasServiceRequest = customerRequests.some((sr: any) => {
                                        const srCategory = sr.serviceType?.category;
                                        if (serviceType === 'mowing') return srCategory === 'mowing';
                                        if (serviceType === 'fertilizing') return srCategory === 'fertilizing';
                                        if (serviceType === 'snow_removal') return srCategory === 'snow_removal';
                                        return false;
                                      });
                                      
                                      return hasServiceRequest;
                                    };
                                    
                                    // Helper to get service price (from servicePricing or service request)
                                    const getServicePrice = (serviceType: 'mowing' | 'fertilizing' | 'snow_removal'): number | null => {
                                      // Try servicePricing first
                                      if (serviceType === 'mowing' && servicePricing?.lawnMowingTrimming?.price) {
                                        return Number(servicePricing.lawnMowingTrimming.price);
                                      }
                                      if (serviceType === 'fertilizing' && servicePricing?.weedControlFertilizing?.price) {
                                        return Number(servicePricing.weedControlFertilizing.price);
                                      }
                                      if (serviceType === 'snow_removal' && servicePricing?.snowRemoval?.price) {
                                        return Number(servicePricing.snowRemoval.price);
                                      }
                                      
                                      // Fallback to service request's accepted bid
                                      const serviceRequest = customerRequests.find((sr: any) => {
                                        const srCategory = sr.serviceType?.category;
                                        if (serviceType === 'mowing') return srCategory === 'mowing';
                                        if (serviceType === 'fertilizing') return srCategory === 'fertilizing';
                                        if (serviceType === 'snow_removal') return srCategory === 'snow_removal';
                                        return false;
                                      });
                                      
                                      if (serviceRequest?.acceptedBidId) {
                                        // Try to get price from accepted bid
                                        const bid = serviceRequest.acceptedBidId;
                                        if (bid.servicePricing && Array.isArray(bid.servicePricing)) {
                                          const matchingService = bid.servicePricing.find((sp: any) => {
                                            if (serviceType === 'mowing') return sp.service === 'lawnMowingTrimming' || sp.serviceName?.toLowerCase().includes('mowing');
                                            if (serviceType === 'fertilizing') return sp.service === 'weedControlFertilizing' || sp.serviceName?.toLowerCase().includes('fertiliz') || sp.serviceName?.toLowerCase().includes('weed');
                                            if (serviceType === 'snow_removal') return sp.service === 'snowRemoval' || sp.serviceName?.toLowerCase().includes('snow');
                                            return false;
                                          });
                                          if (matchingService?.price) {
                                            return Number(matchingService.price);
                                          }
                                        }
                                        if (bid.price) {
                                          return Number(bid.price);
                                        }
                                      }
                                      
                                      return null;
                                    };
                                    
                                    // Helper to get service frequency (from servicePricing or service request)
                                    const getServiceFrequency = (serviceType: 'mowing' | 'fertilizing' | 'snow_removal'): string | null => {
                                      // Try servicePricing first
                                      if (serviceType === 'mowing' && servicePricing?.lawnMowingTrimming?.frequency) {
                                        return servicePricing.lawnMowingTrimming.frequency;
                                      }
                                      if (serviceType === 'fertilizing' && servicePricing?.weedControlFertilizing?.frequency) {
                                        return servicePricing.weedControlFertilizing.frequency;
                                      }
                                      if (serviceType === 'snow_removal' && servicePricing?.snowRemoval?.frequency) {
                                        return servicePricing.snowRemoval.frequency;
                                      }
                                      
                                      // Fallback to service request
                                      const serviceRequest = customerRequests.find((sr: any) => {
                                        const srCategory = sr.serviceType?.category;
                                        if (serviceType === 'mowing') return srCategory === 'mowing';
                                        if (serviceType === 'fertilizing') return srCategory === 'fertilizing';
                                        if (serviceType === 'snow_removal') return srCategory === 'snow_removal';
                                        return false;
                                      });
                                      
                                      if (serviceRequest?.frequency) {
                                        return serviceRequest.frequency;
                                      }
                                      
                                      if (serviceRequest?.acceptedBidId?.servicePricing) {
                                        const bid = serviceRequest.acceptedBidId;
                                        if (bid.servicePricing && Array.isArray(bid.servicePricing)) {
                                          const matchingService = bid.servicePricing.find((sp: any) => {
                                            if (serviceType === 'mowing') return sp.service === 'lawnMowingTrimming' || sp.serviceName?.toLowerCase().includes('mowing');
                                            if (serviceType === 'fertilizing') return sp.service === 'weedControlFertilizing' || sp.serviceName?.toLowerCase().includes('fertiliz') || sp.serviceName?.toLowerCase().includes('weed');
                                            if (serviceType === 'snow_removal') return sp.service === 'snowRemoval' || sp.serviceName?.toLowerCase().includes('snow');
                                            return false;
                                          });
                                          if (matchingService?.frequency) {
                                            return matchingService.frequency;
                                          }
                                        }
                                      }
                                      
                                      return null;
                                    };
                                    
                                    const services = [];
                                    
                                    // Check each service type (same logic as CustomerManagement)
                                    if (customerHasService('mowing')) {
                                      const price = getServicePrice('mowing');
                                      const frequency = getServiceFrequency('mowing');
                                      const frequencyLabel = frequency === 'per-instance' ? 'Per Instance' : 
                                                             frequency === 'weekly' ? 'Weekly' :
                                                             frequency === 'bi-weekly' ? 'Bi-Weekly' :
                                                             frequency === 'monthly' ? 'Monthly' :
                                                             frequency === 'seasonal-contract' ? 'Seasonal Contract' :
                                                             frequency || 'N/A';
                                      services.push(
                                        <Typography key="mowing" variant="body2" sx={{ mb: 0.5 }}>
                                          <strong>Lawn Mowing/Trimming:</strong> ${price ? price.toFixed(2) : 'N/A'} ({frequencyLabel})
                                        </Typography>
                                      );
                                    }
                                    
                                    if (customerHasService('fertilizing')) {
                                      const price = getServicePrice('fertilizing');
                                      const frequency = getServiceFrequency('fertilizing');
                                      const frequencyLabel = frequency === 'per-instance' ? 'Per Instance' : 
                                                             frequency === 'weekly' ? 'Weekly' :
                                                             frequency === 'bi-weekly' ? 'Bi-Weekly' :
                                                             frequency === 'monthly' ? 'Monthly' :
                                                             frequency === 'seasonal-contract' ? 'Seasonal Contract' :
                                                             frequency || 'N/A';
                                      services.push(
                                        <Typography key="weed" variant="body2" sx={{ mb: 0.5 }}>
                                          <strong>Weed Control/Fertilizer:</strong> ${price ? price.toFixed(2) : 'N/A'} ({frequencyLabel})
                                        </Typography>
                                      );
                                    }
                                    
                                    if (customerHasService('snow_removal')) {
                                      const price = getServicePrice('snow_removal');
                                      const frequency = getServiceFrequency('snow_removal');
                                      const frequencyLabel = frequency === 'per-instance' ? 'Per Instance' : 
                                                             frequency === 'weekly' ? 'Weekly' :
                                                             frequency === 'bi-weekly' ? 'Bi-Weekly' :
                                                             frequency === 'monthly' ? 'Monthly' :
                                                             frequency === 'seasonal-contract' ? 'Seasonal Contract' :
                                                             frequency || 'N/A';
                                      services.push(
                                        <Typography key="snow" variant="body2" sx={{ mb: 0.5 }}>
                                          <strong>Snow Removal:</strong> ${price ? price.toFixed(2) : 'N/A'} ({frequencyLabel})
                                        </Typography>
                                      );
                                    }
                                    
                                    if (services.length === 0) {
                                      return (
                                        <Typography variant="body2" color="text.secondary">
                                          No services found
                                        </Typography>
                                      );
                                    }
                                    
                                    return <Box>{services}</Box>;
                                  })()}
                                </TableCell>
                                <TableCell>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <Tooltip title={getAccountStatusUtil(customer).tooltip}>
                                      <Chip 
                                        label={getAccountStatusUtil(customer).label}
                                        color={getAccountStatusUtil(customer).color}
                                        size="small" 
                                        icon={getAccountStatusUtil(customer).icon}
                                      />
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box display="flex" alignItems="center" gap={0.5}>
                                    <PaymentVerificationStatus
                                      verificationStatus={customer.paymentMethodDetails?.verificationStatus}
                                      verificationDate={customer.paymentMethodDetails?.verificationDate}
                                      verificationAmount={customer.paymentMethodDetails?.verificationAmount}
                                      cardLast4={customer.paymentMethodDetails?.cardLast4}
                                      cardBrand={customer.paymentMethodDetails?.cardBrand}
                                    />
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box display="flex" gap={0.5}>
                                    <Tooltip title="Remove Customer">
                                      <IconButton 
                                        onClick={() => {
                                          // Admin can remove customers if needed
                                          if (window.confirm(`Are you sure you want to remove ${customer.firstName} ${customer.lastName}?`)) {
                                            console.log('Remove customer:', customer.id || customer._id);
                                            // TODO: Implement remove customer functionality for Admin
                                          }
                                        }}
                                        color="error"
                                        size="small"
                                        disabled={!customer.id && !customer._id}
                                      >
                                        <Delete />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </CardContent>
                </Card>
              )}
            </Box>
          )}

          {activeTab === 6 && (
            <Box>
              <RouteOptimizationDashboard providerId={providerId} />
            </Box>
          )}

          {activeTab === 7 && (
            <ViolationsTab violations={violations} providerId={providerId} onRefresh={fetchProviderDetails} />
          )}

          {activeTab === 8 && analytics && (
            <Box>
              <Typography variant="h6" gutterBottom>Business Analytics</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h4" color="primary.main">
                        ${analytics.paymentStats.totalRevenue.toFixed(2)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Revenue
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h4" color="success.main">
                        ${analytics.paymentStats.totalEarnings.toFixed(2)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Provider Earnings
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h4" color="info.main">
                        {analytics.paymentStats.transactionCount}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Transactions
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h4" color="secondary.main">
                        ${analytics.paymentStats.averageTransaction.toFixed(2)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Avg Transaction
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Typography variant="h6" gutterBottom>Recent Payments</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Charge = customer was billed. Payout = whether the payment can appear on Weekly Payouts (settled + holds) or was already transferred.
              </Typography>
              {analytics.recentPayments && analytics.recentPayments.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Service</TableCell>
                        <TableCell>Customer</TableCell>
                        <TableCell>Your Earnings</TableCell>
                        <TableCell>Platform Fee</TableCell>
                        <TableCell>Charge</TableCell>
                        <TableCell>Payout</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analytics.recentPayments.map((payment: any) => {
                        // Get service name - same logic as provider dashboard
                        const getServiceName = () => {
                          // Priority 1: Use the stored serviceType from the payment record
                          if (payment.serviceType) {
                            const categoryMap: { [key: string]: string } = {
                              'mowing': 'Lawn Mowing/Trimming',
                              'fertilizing': 'Weed Control/Fertilizing',
                              'snow_removal': 'Snow Removal',
                              'other': payment.serviceType.name || 'Other Service'
                            };
                            return categoryMap[payment.serviceType.category] || payment.serviceType.name || 'Service';
                          }
                          
                          // Priority 2: Fallback to serviceRequestId.serviceType
                          if (payment.serviceRequestId?.serviceType) {
                            const categoryMap: { [key: string]: string } = {
                              'mowing': 'Lawn Mowing/Trimming',
                              'fertilizing': 'Weed Control/Fertilizing',
                              'snow_removal': 'Snow Removal',
                              'other': payment.serviceRequestId.serviceType.name || 'Other Service'
                            };
                            return categoryMap[payment.serviceRequestId.serviceType.category] || payment.serviceRequestId.serviceType.name || 'Service';
                          }
                          
                          // Priority 3: Check onboardedServices (from backend for onboarded customers)
                          if (payment.onboardedServices && Array.isArray(payment.onboardedServices) && payment.onboardedServices.length > 0) {
                            return payment.onboardedServices.join(', ');
                          }
                          
                          // Final fallback
                          return payment.serviceRequestId?.serviceType?.name || 'Service';
                        };

                        const settlement = payment.stripeSettlementStatus || 'pending';
                        const payoutLabel = payment.payoutId
                          ? 'Paid out'
                          : settlement === 'settled'
                            ? 'Ready (after hold)'
                            : settlement === 'failed' || settlement === 'chargeback'
                              ? settlement
                              : 'Settling';

                        return (
                          <TableRow key={payment._id}>
                            <TableCell>
                              {formatDateOnly(payment.completedAt || payment.createdAt)}
                            </TableCell>
                            <TableCell>
                              {getServiceName()}
                            </TableCell>
                            <TableCell>
                              {payment.customerId?.firstName} {payment.customerId?.lastName}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                                ${(payment.providerCut || payment.providerPayout || 0).toFixed(2)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                ${(payment.platformCut || payment.platformFee || 0).toFixed(2)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={
                                  payment.type === 'seasonal_contract_subscription_payment'
                                    ? 'Monthly (seasonal)'
                                    : payment.status === 'skip' && payment.type === 'seasonal_contract_subscription_skip'
                                      ? 'Monthly Payment Plan'
                                      : payment.status || 'completed'
                                }
                                color={
                                  payment.type === 'seasonal_contract_subscription_payment'
                                    ? 'default'
                                    : payment.status === 'skip' && payment.type === 'seasonal_contract_subscription_skip'
                                      ? 'info'
                                      : payment.status === 'completed'
                                      ? 'success'
                                      : 'warning'
                                }
                                size="small"
                                sx={
                                  payment.type === 'seasonal_contract_subscription_payment'
                                    ? { backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 500 }
                                    : undefined
                                }
                              />
                            </TableCell>
                            <TableCell>
                              <Tooltip
                                title={
                                  payment.payoutId
                                    ? 'Already included in a provider payout transfer'
                                    : settlement === 'settled'
                                      ? 'Charge settled in LawnHub. Appears on Weekly Payouts after hold rules (first payout 7 days; all payments 3 days after settled date).'
                                      : 'Customer charged, but LawnHub settlement flag is still pending — Weekly Payouts will not list this until settled.'
                                }
                              >
                                <Chip
                                  label={payoutLabel}
                                  size="small"
                                  color={
                                    payment.payoutId
                                      ? 'default'
                                      : settlement === 'settled'
                                        ? 'info'
                                        : settlement === 'failed' || settlement === 'chargeback'
                                          ? 'error'
                                          : 'warning'
                                  }
                                  variant={payment.payoutId ? 'outlined' : 'filled'}
                                />
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">No payment history available.</Alert>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>

    {/* Document Viewer Dialog */}
    <Dialog
      open={documentDialogOpen}
      onClose={() => {
        if (documentBlobUrl) {
          window.URL.revokeObjectURL(documentBlobUrl);
          setDocumentBlobUrl(null);
        }
        setDocumentUrl(null);
        setDocumentDialogOpen(false);
      }}
      maxWidth="lg"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          height: '90vh',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">{documentTitle}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {documentUrl && documentTitle.includes('W-9') && data?.provider && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={async () => {
                try {
                  const token = localStorage.getItem('token');
                  const base = process.env.REACT_APP_API_URL
                    ? (process.env.REACT_APP_API_URL.endsWith('/api') ? process.env.REACT_APP_API_URL : process.env.REACT_APP_API_URL + '/api')
                    : window.location.origin + '/api';
                  const r = await fetch(`${base}/compliance/provider/${providerId}/w9-document`, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  if (!r.ok) throw new Error('Download failed');
                  const blob = await r.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `W-9-${data.provider.businessName || data.provider.firstName || 'provider'}.pdf`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                } catch (err: any) {
                  setDocumentError(err.message || 'Failed to download');
                }
              }}
            >
              Download
            </Button>
          )}
          {documentUrl && !documentTitle.includes('W-9') && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileDownload />}
              href={documentUrl}
              download
              component="a"
            >
              Download
            </Button>
          )}
          <IconButton
            onClick={() => {
              if (documentBlobUrl) {
                window.URL.revokeObjectURL(documentBlobUrl);
                setDocumentBlobUrl(null);
              }
              setDocumentUrl(null);
              setDocumentDialogOpen(false);
            }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, height: '100%' }}>
        {documentUrl && (
          <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <iframe
              src={documentUrl}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                minHeight: '80vh',
              }}
              title={documentTitle}
            />
          </Box>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
};

export default ProviderDetailDialog;

